import { useContext } from "react";
import { EventContext } from "@/providers/EventProvider";

/** Start nudging this many days before the event plan window closes. */
const WARN_WITHIN_DAYS = 14;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export interface ExpiringEventPlanState {
  /** Show the approaching-expiry warning banner (paid, within the window, not yet locked). */
  show: boolean;
  /** The plan has already expired and the event is read-only — show the locked banner instead. */
  locked: boolean;
  daysRemaining: number;
  /** Machine planType (e.g. "EVENT_PROFESSIONAL") — humanize for display. */
  plan: string | null;
  eventId: string | null;
}

const HIDDEN: ExpiringEventPlanState = {
  show: false,
  locked: false,
  daysRemaining: 0,
  plan: null,
  eventId: null,
};

/**
 * Event-scoped twin of {@link useExpiringSubscription}. Reads the active event
 * from {@link EventContext} (the layout already fetched planExpiresAt/planLocked/
 * planType) and reports whether its 9-month plan window is approaching expiry
 * (`show`, within {@link WARN_WITHIN_DAYS} days) or has already lapsed
 * (`locked`). Grandfathered / FREE events (null `planExpiresAt`) never warn.
 * Returns HIDDEN outside an event surface so it's safe to call unconditionally.
 */
export function useExpiringEventPlan(): ExpiringEventPlanState {
  const ctx = useContext(EventContext);
  const event = ctx?.event;
  if (!event) return HIDDEN;

  const eventId = event.eventId ?? null;
  const plan = event.planType ?? null;

  // Already locked: the cron sweep flipped planLocked once the window closed.
  if (event.planLocked === true) {
    return { show: false, locked: true, daysRemaining: 0, plan, eventId };
  }

  // No plan window (FREE / grandfathered) ⇒ never expires.
  if (!event.planExpiresAt) return HIDDEN;
  const expiresMs = new Date(event.planExpiresAt).getTime();
  if (Number.isNaN(expiresMs)) return HIDDEN;

  const daysRemaining = Math.ceil((expiresMs - Date.now()) / ONE_DAY_MS);
  // Past expiry but not yet swept to locked → treat as locked-imminent so the
  // user still sees the urgent banner (daysRemaining clamps to 0).
  if (daysRemaining < 0) {
    return { show: true, locked: false, daysRemaining: 0, plan, eventId };
  }
  if (daysRemaining > WARN_WITHIN_DAYS) return HIDDEN;

  return { show: true, locked: false, daysRemaining, plan, eventId };
}
